import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import {
  Plus,
  FileText,
  AlertTriangle,
  Clock,
  CheckCircle,
  XCircle,
  Filter,
  ChevronRight,
  AlertOctagon,
} from 'lucide-react';

import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { formatDate, formatDateTime } from '@/lib/utils';

const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:3001';

// Typen
interface VgoWorkflow {
  id: string;
  workflowNumber: string;
  workflowType: string;
  status: string;
  priority: string;
  incidentType: string;
  incidentDate: string;
  description: string;
  dueDate: string | null;
  createdAt: string;
  prisoner: {
    id: string;
    prisonerNumber: string;
    firstName: string;
    lastName: string;
  };
  createdBy: {
    displayName: string;
    username: string;
  };
}

interface WorkflowStats {
  totalOpen: number;
  overdue: number;
  recentCount: number;
  urgentCount: number;
  byStatus: { status: string; count: number }[];
  byType: { type: string; label: string; count: number }[];
}

// Labels
const STATUS_LABELS: Record<string, string> = {
  ENTWURF: 'Entwurf',
  EINGEREICHT: 'Eingereicht',
  IN_PRUEFUNG: 'In Prüfung',
  WEITERGELEITET: 'Weitergeleitet',
  ABGESCHLOSSEN: 'Abgeschlossen',
  STORNIERT: 'Storniert',
};

const WORKFLOW_TYPE_LABELS: Record<string, string> = {
  BESONDERES_VORKOMMNIS: 'Besonderes Vorkommnis',
  AUFFAELLIGKEIT: 'Auffälligkeit',
  DISZIPLINAR_ANZEIGE: 'Disziplinaranzeige',
  BESUCH_VORKOMMNIS: 'Besuchsvorkommnis',
  VERLEGUNG_ANREGUNG: 'Verlegungsanregung',
  ARBEIT_VORKOMMNIS: 'Arbeitsvorkommnis',
  EILMELDUNG: 'Eilmeldung',
};

const PRIORITY_LABELS: Record<string, string> = {
  NIEDRIG: 'Niedrig',
  NORMAL: 'Normal',
  HOCH: 'Hoch',
  EILSACHE: 'Eilsache',
};

// API-Funktionen
const fetchWorkflows = async (params: Record<string, string>) => {
  const token = localStorage.getItem('token');
  const searchParams = new URLSearchParams(params);
  
  const response = await fetch(`${API_URL}/api/v1/vgo-workflows?${searchParams}`, {
    headers: {
      'Content-Type': 'application/json',
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    },
  });
  
  if (!response.ok) throw new Error('Fehler beim Laden der Vorgänge');
  return response.json();
};

const fetchStatistics = async () => {
  const token = localStorage.getItem('token');
  
  const response = await fetch(`${API_URL}/api/v1/vgo-workflows/statistics`, {
    headers: {
      'Content-Type': 'application/json',
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    },
  });
  
  if (!response.ok) throw new Error('Fehler beim Laden der Statistiken');
  return response.json();
};

// Status-Badge Komponente
function StatusBadge({ status }: { status: string }) {
  const variants: Record<string, { variant: 'default' | 'secondary' | 'destructive' | 'outline'; icon: React.ReactNode }> = {
    ENTWURF: { variant: 'outline', icon: <FileText className="h-3 w-3" /> },
    EINGEREICHT: { variant: 'secondary', icon: <Clock className="h-3 w-3" /> },
    IN_PRUEFUNG: { variant: 'default', icon: <AlertTriangle className="h-3 w-3" /> },
    WEITERGELEITET: { variant: 'secondary', icon: <ChevronRight className="h-3 w-3" /> },
    ABGESCHLOSSEN: { variant: 'default', icon: <CheckCircle className="h-3 w-3" /> },
    STORNIERT: { variant: 'destructive', icon: <XCircle className="h-3 w-3" /> },
  };

  const { variant, icon } = variants[status] || { variant: 'outline', icon: null };

  return (
    <Badge variant={variant} className="flex items-center gap-1">
      {icon}
      {STATUS_LABELS[status] || status}
    </Badge>
  );
}

// Prioritäts-Badge
function PriorityBadge({ priority }: { priority: string }) {
  if (priority === 'NORMAL' || priority === 'NIEDRIG') return null;
  
  return (
    <Badge 
      variant={priority === 'EILSACHE' ? 'destructive' : 'secondary'}
      className="flex items-center gap-1"
    >
      {priority === 'EILSACHE' && <AlertOctagon className="h-3 w-3" />}
      {PRIORITY_LABELS[priority]}
    </Badge>
  );
}

export function VgoWorkflowsPage() {
  const [statusFilter, setStatusFilter] = useState<string>('');
  const [typeFilter, setTypeFilter] = useState<string>('');
  const [page, setPage] = useState(1);

  // Statistiken laden
  const { data: stats } = useQuery<WorkflowStats>({
    queryKey: ['vgo-statistics'],
    queryFn: fetchStatistics,
  });

  // Workflows laden
  const { data: workflowsData, isLoading } = useQuery({
    queryKey: ['vgo-workflows', statusFilter, typeFilter, page],
    queryFn: () => fetchWorkflows({
      ...(statusFilter && { status: statusFilter }),
      ...(typeFilter && { workflowType: typeFilter }),
      page: page.toString(),
      pageSize: '20',
    }),
  });

  const workflows = workflowsData?.data || [];
  const totalPages = workflowsData?.totalPages || 1;

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold">Vorgänge (VGO)</h1>
          <p className="text-muted-foreground">
            Vollzugsgeschäftsordnung-konforme Vorgangsbearbeitung
          </p>
        </div>
        <Button asChild>
          <Link to="/vgo-workflows/new">
            <Plus className="mr-2 h-4 w-4" />
            Neuer Vorgang
          </Link>
        </Button>
      </div>

      {/* Statistik-Kacheln */}
      {stats && (
        <div className="grid gap-4 md:grid-cols-4">
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium">Offene Vorgänge</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-3xl font-bold">{stats.totalOpen}</div>
            </CardContent>
          </Card>

          <Card className={stats.overdue > 0 ? 'border-red-300 bg-red-50' : ''}>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium flex items-center gap-2">
                <Clock className="h-4 w-4" />
                Überfällig
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className={`text-3xl font-bold ${stats.overdue > 0 ? 'text-red-600' : ''}`}>
                {stats.overdue}
              </div>
            </CardContent>
          </Card>

          <Card className={stats.urgentCount > 0 ? 'border-amber-300 bg-amber-50' : ''}>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium flex items-center gap-2">
                <AlertOctagon className="h-4 w-4" />
                Eilsachen
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className={`text-3xl font-bold ${stats.urgentCount > 0 ? 'text-amber-600' : ''}`}>
                {stats.urgentCount}
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium">Letzte 7 Tage</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-3xl font-bold">{stats.recentCount}</div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Filter */}
      <Card>
        <CardContent className="pt-6">
          <div className="flex gap-4 flex-wrap">
            <Select value={statusFilter || '__all__'} onValueChange={(v) => setStatusFilter(v === '__all__' ? '' : v)}>
              <SelectTrigger className="w-48">
                <SelectValue placeholder="Status filtern" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="__all__">Alle Status</SelectItem>
                {Object.entries(STATUS_LABELS).map(([value, label]) => (
                  <SelectItem key={value} value={value}>{label}</SelectItem>
                ))}
              </SelectContent>
            </Select>

            <Select value={typeFilter || '__all__'} onValueChange={(v) => setTypeFilter(v === '__all__' ? '' : v)}>
              <SelectTrigger className="w-56">
                <SelectValue placeholder="Vorgangsart filtern" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="__all__">Alle Vorgangsarten</SelectItem>
                {Object.entries(WORKFLOW_TYPE_LABELS).map(([value, label]) => (
                  <SelectItem key={value} value={value}>{label}</SelectItem>
                ))}
              </SelectContent>
            </Select>

            {(statusFilter || typeFilter) && (
              <Button 
                variant="ghost" 
                onClick={() => { setStatusFilter(''); setTypeFilter(''); }}
              >
                Filter zurücksetzen
              </Button>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Vorgangsliste */}
      <Card>
        <CardHeader>
          <CardTitle>Vorgänge</CardTitle>
          <CardDescription>
            {workflowsData?.total || 0} Vorgänge gefunden
          </CardDescription>
        </CardHeader>
        <CardContent>
          {isLoading ? (
            <div className="py-8 text-center text-muted-foreground">Lädt...</div>
          ) : workflows.length === 0 ? (
            <div className="py-8 text-center text-muted-foreground">
              Keine Vorgänge gefunden
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b text-left text-sm text-muted-foreground">
                    <th className="pb-3 pr-4">Vorgangsnummer</th>
                    <th className="pb-3 pr-4">Art</th>
                    <th className="pb-3 pr-4">Gefangener</th>
                    <th className="pb-3 pr-4">Datum</th>
                    <th className="pb-3 pr-4">Status</th>
                    <th className="pb-3 pr-4">Priorität</th>
                    <th className="pb-3 pr-4">Erstellt von</th>
                    <th className="pb-3"></th>
                  </tr>
                </thead>
                <tbody>
                  {workflows.map((workflow: VgoWorkflow) => (
                    <tr key={workflow.id} className="border-b hover:bg-muted/50">
                      <td className="py-3 pr-4">
                        <Link 
                          to={`/vgo-workflows/${workflow.id}`}
                          className="font-mono text-primary hover:underline"
                        >
                          {workflow.workflowNumber}
                        </Link>
                      </td>
                      <td className="py-3 pr-4 text-sm">
                        {WORKFLOW_TYPE_LABELS[workflow.workflowType] || workflow.workflowType}
                      </td>
                      <td className="py-3 pr-4">
                        <Link 
                          to={`/prisoners/${workflow.prisoner.id}`}
                          className="text-sm hover:underline"
                        >
                          {workflow.prisoner.lastName}, {workflow.prisoner.firstName}
                        </Link>
                        <p className="text-xs text-muted-foreground">
                          {workflow.prisoner.prisonerNumber}
                        </p>
                      </td>
                      <td className="py-3 pr-4 text-sm">
                        {formatDate(workflow.incidentDate)}
                      </td>
                      <td className="py-3 pr-4">
                        <StatusBadge status={workflow.status} />
                      </td>
                      <td className="py-3 pr-4">
                        <PriorityBadge priority={workflow.priority} />
                      </td>
                      <td className="py-3 pr-4 text-sm text-muted-foreground">
                        {workflow.createdBy?.displayName || workflow.createdBy?.username}
                      </td>
                      <td className="py-3">
                        <Button variant="ghost" size="sm" asChild>
                          <Link to={`/vgo-workflows/${workflow.id}`}>
                            <ChevronRight className="h-4 w-4" />
                          </Link>
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="flex justify-center gap-2 mt-4">
              <Button
                variant="outline"
                size="sm"
                onClick={() => setPage(p => Math.max(1, p - 1))}
                disabled={page === 1}
              >
                Zurück
              </Button>
              <span className="py-2 px-4 text-sm">
                Seite {page} von {totalPages}
              </span>
              <Button
                variant="outline"
                size="sm"
                onClick={() => setPage(p => Math.min(totalPages, p + 1))}
                disabled={page === totalPages}
              >
                Weiter
              </Button>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
